import moment from 'moment/moment';
import {McpLimitActionType} from 'src/openapi/admin/models/McpLimitActionType';
import {McpLimitType} from 'src/openapi/admin/models/McpLimitType';
import {mapDateFormat} from 'src/utils/mapDateFormat';

import type {AccountMcpLimitLogHits} from 'src/openapi/admin/models/AccountMcpLimitLogHits';
import type {UserMcpLimitLogHits} from 'src/openapi/admin/models/UserMcpLimitLogHits';

export type DateFormat = 'DATE' | 'DATE_TIME';

export type McpLimitLogEvent = UserMcpLimitLogHits | AccountMcpLimitLogHits;

const USER_ACTIONS = new Set<string>([
    McpLimitActionType.USER_LIMIT_CREATE,
    McpLimitActionType.USER_LIMIT_UPDATE,
    McpLimitActionType.TEMPORARY_USER_LIMIT_CREATE,
    McpLimitActionType.TEMPORARY_USER_LIMIT_UPDATE,
]);

export const isUserEvent = (event: McpLimitLogEvent): event is UserMcpLimitLogHits =>
    USER_ACTIONS.has(event.actionName);

const isExpirationField = (fieldName: string): boolean => {
    const lower = fieldName.toLowerCase();
    return lower.includes('expir') || lower.includes('end date');
};

export const getLimitChange = (event: McpLimitLogEvent) => {
    return event.changeInfo?.find(c => !isExpirationField(c.fieldName));
};

export const getExpirationChange = (event: McpLimitLogEvent) => {
    return event.changeInfo?.find(c => isExpirationField(c.fieldName));
};

export const getAccountEmail = (event: McpLimitLogEvent): string => {
    if (isUserEvent(event)) {
        return (event as UserMcpLimitLogHits).targetUserEmail || '';
    }
    return (event as AccountMcpLimitLogHits).targetAccountName || '';
};

export const MCP_LIMIT_TYPE_LABELS: Record<McpLimitType, string> = {
    [McpLimitType.MCP_PREM_M]: 'MCP Premium',
    [McpLimitType.MCP_CORP_M]: 'Select MCP',
};

type McpLimitLogFiltersForApi = {
    limitTypes?: McpLimitType[],
    startDate?: string,
    endDate?: string,
    userIds?: number[],
    accountIds?: number[],
};

export const formatUtcDate = (
    value: string,
    dateFormat: string,
    type: DateFormat,
): string =>
    moment.utc(value).format(mapDateFormat(dateFormat, 'WORD', type));

export const mapFiltersToApiRequest = (
    filters: Record<string, unknown> | undefined,
): McpLimitLogFiltersForApi => {
    if (!filters) {
        return {};
    }
    return {
        limitTypes: filters.limitTypes as McpLimitType[] | undefined,
        startDate: filters.startDate as string | undefined,
        endDate: filters.endDate as string | undefined,
        userIds: filters.userIds as number[] | undefined,
        accountIds: filters.accountIds as number[] | undefined,
    };
};
